import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getZonasEnvioActivas } from "../../../services/usuarios_tienda/zonas_envio";
import { formatearPrecio } from "../../../utils/moneda";

const EMPTY_FORM = {
  tienda_zona_envio_id: "",
  disponible: 1,
  usa_costo_base: 1,
  recargo: "0",
  costo_final: "",
  estado: 1,
};

function normalizeRuleData(ruleData, metodoData) {
  const costoBase = Number(metodoData?.costo_base ?? 0);

  if (!ruleData) {
    return {
      ...EMPTY_FORM,
      costo_final: String(costoBase.toFixed(2)),
    };
  }

  return {
    tienda_zona_envio_id:
      ruleData.tienda_zona_envio_id !== undefined &&
      ruleData.tienda_zona_envio_id !== null
        ? String(ruleData.tienda_zona_envio_id)
        : "",
    disponible: Number(ruleData.disponible ?? 1) === 1 ? 1 : 0,
    usa_costo_base: Number(ruleData.usa_costo_base ?? 1) === 1 ? 1 : 0,
    recargo:
      ruleData.recargo !== undefined && ruleData.recargo !== null
        ? String(ruleData.recargo)
        : "0",
    costo_final:
      ruleData.costo_final !== undefined && ruleData.costo_final !== null
        ? String(ruleData.costo_final)
        : String(costoBase.toFixed(2)),
    estado: Number(ruleData.estado ?? 1) === 1 ? 1 : 0,
  };
}

function getErrorMessage(error, fallback = "Ocurrió un error inesperado.") {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function ShippingRuleModal({
  isOpen,
  onClose,
  onSubmit,
  metodoData,
  ruleData,
  configuracionTienda,
  zonas: zonasOverride = [],
}) {
  const isEdit = !!ruleData;
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [alert, setAlert] = useState(null);
  const [saving, setSaving] = useState(false);
  const [zonasActivas, setZonasActivas] = useState([]);
  const [loadingZonas, setLoadingZonas] = useState(false);
  const alertTimerRef = useRef(null);

  const metodoEnvioId = Number(metodoData?.tienda_metodo_envio_id ?? 0);

  const zonas =
    Array.isArray(zonasOverride) && zonasOverride.length > 0
      ? zonasOverride
      : zonasActivas;

  const costoBaseMetodo = Number(metodoData?.costo_base ?? 0);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4500);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setForm(normalizeRuleData(ruleData, metodoData));
    setAlert(null);
    setSaving(false);

    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, [isOpen, ruleData, metodoData]);

  useEffect(() => {
    if (!isOpen) return;
    if (Array.isArray(zonasOverride) && zonasOverride.length > 0) return;

    let cancelled = false;
    setLoadingZonas(true);

    (async () => {
      try {
        const data = await getZonasEnvioActivas({});
        if (cancelled) return;
        setZonasActivas(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error cargando zonas de envío activas:", error);
        if (!cancelled) {
          setZonasActivas([]);
          showAlert(
            "error",
            getErrorMessage(
              error,
              "No se pudieron cargar las zonas de envío."
            )
          );
        }
      } finally {
        if (!cancelled) setLoadingZonas(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, zonasOverride?.length, showAlert]);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, saving]);

  const selectedZona = useMemo(() => {
    const zonaId = Number(form.tienda_zona_envio_id);
    return zonas.find((z) => Number(z.tienda_zona_envio_id) === zonaId) || null;
  }, [form.tienda_zona_envio_id, zonas]);

  const computedPreview = useMemo(() => {
    const recargo = Number(form.recargo);
    const costoFinalManual = Number(form.costo_final);
    const usaCostoBase = Number(form.usa_costo_base) === 1;

    const finalCost = usaCostoBase
      ? (Number.isFinite(costoBaseMetodo) ? costoBaseMetodo : 0) +
        (Number.isFinite(recargo) ? recargo : 0)
      : Number.isFinite(costoFinalManual)
      ? costoFinalManual
      : 0;

    return {
      usaCostoBase,
      costoBase: costoBaseMetodo,
      recargo: Number.isFinite(recargo) ? recargo : 0,
      costoFinal: finalCost,
      disponible: Number(form.disponible) === 1,
      estado: Number(form.estado) === 1,
    };
  }, [form, costoBaseMetodo]);

  const updateField = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "recargo" && Number(prev.usa_costo_base) === 1) {
        const recargo = Number(value);
        const finalCost =
          (Number.isFinite(costoBaseMetodo) ? costoBaseMetodo : 0) +
          (Number.isFinite(recargo) ? recargo : 0);

        next.costo_final = String(finalCost.toFixed(2));
      }

      return next;
    });
  };

  const toggleBool = (name) => {
    setForm((prev) => {
      const nextValue = Number(prev[name]) === 1 ? 0 : 1;
      const next = { ...prev, [name]: nextValue };

      if (name === "usa_costo_base" && nextValue === 1) {
        const recargo = Number(next.recargo);
        const finalCost =
          (Number.isFinite(costoBaseMetodo) ? costoBaseMetodo : 0) +
          (Number.isFinite(recargo) ? recargo : 0);

        next.costo_final = String(finalCost.toFixed(2));
      }

      return next;
    });
  };

  const validateForm = () => {
    const errors = [];

    const zonaId = Number(form.tienda_zona_envio_id);
    const recargoRaw = String(form.recargo ?? "").trim();
    const costoFinalRaw = String(form.costo_final ?? "").trim();

    const recargo = Number(form.recargo);
    const costoFinal = Number(form.costo_final);
    const usaCostoBase = Number(form.usa_costo_base) === 1;

    if (!Number.isInteger(metodoEnvioId) || metodoEnvioId <= 0) {
      errors.push("No se encontró el método de envío.");
    }

    if (!Array.isArray(zonas) || zonas.length === 0) {
      errors.push("No hay zonas activas disponibles para asignar.");
    }

    if (!Number.isInteger(zonaId) || zonaId <= 0) {
      errors.push("Debes seleccionar una zona de envío.");
    }

    if (recargoRaw === "") {
      errors.push("Debes ingresar el recargo.");
    }

    if (!Number.isFinite(recargo) || recargo < 0) {
      errors.push("El recargo debe ser un número mayor o igual a 0.");
    }

    if (!usaCostoBase) {
      if (costoFinalRaw === "") {
        errors.push("Debes ingresar el costo final.");
      }

      if (!Number.isFinite(costoFinal) || costoFinal < 0) {
        errors.push("El costo final debe ser un número mayor o igual a 0.");
      }
    }

    return errors;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setAlert(null);

    const errors = validateForm();
    if (errors.length) {
      showAlert("error", errors[0]);
      return;
    }

    setSaving(true);

    try {
      const usaCostoBase = Number(form.usa_costo_base) === 1;
      const recargo = Number(form.recargo || 0);
      const costoFinal = usaCostoBase
        ? Number((costoBaseMetodo + recargo).toFixed(2))
        : Number(form.costo_final || 0);

      await onSubmit?.({
        tienda_zona_envio_id: Number(form.tienda_zona_envio_id),
        disponible: Number(form.disponible) === 1 ? 1 : 0,
        usa_costo_base: usaCostoBase ? 1 : 0,
        recargo,
        costo_final: costoFinal,
        estado: Number(form.estado) === 1 ? 1 : 0,
      });

      onClose?.();
    } catch (error) {
      console.error("Error al guardar la regla:", error);
      showAlert(
        "error",
        getErrorMessage(error, "No se pudo guardar la regla.")
      );
      setSaving(false);
    }
  };

  const disableSubmit =
    saving ||
    loadingZonas ||
    !Number.isInteger(metodoEnvioId) ||
    metodoEnvioId <= 0 ||
    !Array.isArray(zonas) ||
    zonas.length === 0;

  if (!isOpen) return null;

  return (
    <div
      style={ui.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div style={ui.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={ui.header}>
          <div>
            <h2 style={ui.title}>
              {isEdit ? "Editar regla de envío" : "Agregar regla de envío"}
            </h2>
            <p style={ui.subtitle}>
              Configura disponibilidad y costo para una zona específica.
            </p>
          </div>

          <button
            type="button"
            style={ui.closeBtn}
            onClick={() => !saving && onClose?.()}
            disabled={saving}
          >
            ✕
          </button>
        </div>

        <div style={ui.headerSummary}>
          <div style={ui.summaryGrid}>
            <div style={ui.summaryCard}>
              <span style={ui.summaryLabel}>Método</span>
              <strong style={ui.summaryValue}>
                {metodoData?.nombre_visible || metodoData?.nombre || "Método"}
              </strong>
            </div>

            <div style={ui.summaryCard}>
              <span style={ui.summaryLabel}>Costo base</span>
              <strong style={ui.summaryValue}>
                {formatearPrecio(costoBaseMetodo, configuracionTienda, {
                  espacioEntreSimbolo: true,
                }) || "—"}
              </strong>
            </div>

            <div style={ui.summaryCard}>
              <span style={ui.summaryLabel}>Zona seleccionada</span>
              <strong style={ui.summaryValue}>
                {selectedZona?.nombre || "Sin seleccionar"}
              </strong>
            </div>
          </div>
        </div>

        {alert && (
          <div
            style={{
              ...ui.alert,
              ...(alert.type === "error" ? ui.alertError : ui.alertInfo),
            }}
          >
            <span>{alert.message}</span>
            <button
              style={ui.alertX}
              onClick={() => setAlert(null)}
              disabled={saving}
              type="button"
            >
              ✕
            </button>
          </div>
        )}

        <form onSubmit={submit} style={ui.form}>
          <div style={ui.body}>
            <div style={ui.leftCol}>
              <section style={ui.sectionCard}>
                <div style={ui.sectionHeader}>
                  <h3 style={ui.sectionTitle}>Zona y disponibilidad</h3>
                  <p style={ui.sectionText}>
                    Elige la zona a la que aplicará esta regla.
                  </p>
                </div>

                <div style={ui.inputGroup}>
                  <label style={ui.label}>Zona de envío</label>
                  <select
                    name="tienda_zona_envio_id"
                    value={form.tienda_zona_envio_id}
                    onChange={updateField}
                    style={ui.select}
                    disabled={saving || loadingZonas}
                  >
                    <option value="">
                      {loadingZonas
                        ? "Cargando zonas..."
                        : zonas.length === 0
                        ? "No hay zonas activas"
                        : "Selecciona una zona"}
                    </option>
                    {zonas.map((zona) => (
                      <option
                        key={zona.tienda_zona_envio_id}
                        value={zona.tienda_zona_envio_id}
                      >
                        {zona.nombre}
                        {zona.codigo ? ` • ${zona.codigo}` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={ui.grid2}>
                  <label style={ui.switchCard}>
                    <div>
                      <span style={ui.switchTitle}>Disponible</span>
                      <p style={ui.switchText}>
                        Permite ofrecer este método en la zona.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={Number(form.disponible) === 1}
                      onChange={() => toggleBool("disponible")}
                      disabled={saving}
                    />
                  </label>

                  <label style={ui.switchCard}>
                    <div>
                      <span style={ui.switchTitle}>Regla activa</span>
                      <p style={ui.switchText}>
                        Activa o desactiva esta configuración.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={Number(form.estado) === 1}
                      onChange={() => toggleBool("estado")}
                      disabled={saving}
                    />
                  </label>
                </div>
              </section>

              <section style={ui.sectionCard}>
                <div style={ui.sectionHeader}>
                  <h3 style={ui.sectionTitle}>Configuración de costo</h3>
                  <p style={ui.sectionText}>
                    Define si usarás el costo base del método o un costo fijo.
                  </p>
                </div>

                <label style={ui.switchCard}>
                  <div>
                    <span style={ui.switchTitle}>Usar costo base del método</span>
                    <p style={ui.switchText}>
                      Si está activo, el costo final será costo base + recargo.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={Number(form.usa_costo_base) === 1}
                    onChange={() => toggleBool("usa_costo_base")}
                    disabled={saving}
                  />
                </label>

                <div style={{ ...ui.grid2, marginTop: 16 }}>
                  <div style={ui.inputGroup}>
                    <label style={ui.label}>Recargo</label>
                    <input
                      type="number"
                      name="recargo"
                      value={form.recargo}
                      onChange={updateField}
                      style={ui.input}
                      disabled={saving}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>

                  <div style={ui.inputGroup}>
                    <label style={ui.label}>
                      {Number(form.usa_costo_base) === 1
                        ? "Costo final calculado"
                        : "Costo final"}
                    </label>
                    <input
                      type="number"
                      name="costo_final"
                      value={form.costo_final}
                      onChange={updateField}
                      style={{
                        ...ui.input,
                        ...(Number(form.usa_costo_base) === 1
                          ? ui.inputDisabled
                          : null),
                      }}
                      disabled={saving || Number(form.usa_costo_base) === 1}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </section>
            </div>

            <div style={ui.rightCol}>
              <section style={ui.sideCard}>
                <h3 style={ui.sideTitle}>Vista previa</h3>

                <div style={ui.previewItem}>
                  <span style={ui.previewLabel}>Zona</span>
                  <strong style={ui.previewValue}>
                    {selectedZona?.nombre || "Sin seleccionar"}
                  </strong>
                </div>

                <div style={ui.previewItem}>
                  <span style={ui.previewLabel}>Disponibilidad</span>
                  <strong style={ui.previewValue}>
                    {computedPreview.disponible ? "Disponible" : "No disponible"}
                  </strong>
                </div>

                <div style={ui.previewItem}>
                  <span style={ui.previewLabel}>Modo de cálculo</span>
                  <strong style={ui.previewValue}>
                    {computedPreview.usaCostoBase ? "Base + recargo" : "Costo fijo"}
                  </strong>
                </div>

                <div style={ui.kpiCard}>
                  <span style={ui.kpiLabel}>Costo base</span>
                  <strong style={ui.kpiValue}>
                    {formatearPrecio(computedPreview.costoBase, configuracionTienda, {
                      espacioEntreSimbolo: true,
                    }) || "—"}
                  </strong>
                </div>

                <div style={ui.kpiCard}>
                  <span style={ui.kpiLabel}>Recargo</span>
                  <strong style={ui.kpiValue}>
                    {formatearPrecio(computedPreview.recargo, configuracionTienda, {
                      espacioEntreSimbolo: true,
                    }) || "—"}
                  </strong>
                </div>

                <div style={ui.kpiCardPrimary}>
                  <span style={ui.kpiLabelPrimary}>Costo final</span>
                  <strong style={ui.kpiValuePrimary}>
                    {formatearPrecio(computedPreview.costoFinal, configuracionTienda, {
                      espacioEntreSimbolo: true,
                    }) || "—"}
                  </strong>
                </div>
              </section>
            </div>
          </div>

          <div style={ui.footer}>
            <button
              type="button"
              style={ui.cancelBtn}
              onClick={() => !saving && onClose?.()}
              disabled={saving}
            >
              Cancelar
            </button>

            <button
              type="submit"
              style={{
                ...ui.saveBtn,
                ...(disableSubmit ? ui.saveBtnDisabled : null),
              }}
              disabled={disableSubmit}
            >
              {saving
                ? "Guardando..."
                : isEdit
                ? "Guardar regla"
                : "Agregar regla"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ui = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    zIndex: 2100,
    backdropFilter: "blur(2px)",
  },

  modal: {
    width: "min(950px, 96vw)",
    maxHeight: "92vh",
    background: "#ffffff",
    borderRadius: "20px",
    boxShadow: "0 20px 60px rgba(15,23,42,0.22)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    border: "1px solid rgba(148, 163, 184, 0.18)",
  },

  header: {
    padding: "22px 26px 16px",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    background: "linear-gradient(180deg, #FFFFFF 0%, #FAFCFF 100%)",
  },

  title: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#111827",
    margin: 0,
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#6B7280",
    fontSize: "13px",
    lineHeight: 1.45,
  },

  closeBtn: {
    background: "#F8FAFC",
    border: "1px solid #E2E8F0",
    cursor: "pointer",
    fontSize: "18px",
    color: "#475569",
    width: "38px",
    height: "38px",
    borderRadius: "10px",
    flexShrink: 0,
  },

  headerSummary: {
    padding: "16px 26px",
    borderBottom: "1px solid #E5E7EB",
    background: "#F8FBFF",
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
  },

  summaryCard: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: "14px",
    padding: "12px 14px",
  },

  summaryLabel: {
    display: "block",
    fontSize: "12px",
    color: "#64748B",
    marginBottom: "6px",
    fontWeight: 600,
  },

  summaryValue: {
    display: "block",
    fontSize: "14px",
    color: "#0F172A",
    fontWeight: 700,
    wordBreak: "break-word",
  },

  alert: {
    margin: "16px 26px 0",
    padding: "12px 14px",
    borderRadius: "12px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: 700,
  },

  alertError: {
    background: "#FEF2F2",
    borderColor: "#FECACA",
    color: "#7F1D1D",
  },

  alertInfo: {
    background: "#EFF6FF",
    borderColor: "#BFDBFE",
    color: "#1E3A8A",
  },

  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.75,
  },

  form: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    flex: 1,
  },

  body: {
    padding: "22px 26px",
    overflowY: "auto",
    minHeight: 0,
    flex: 1,
    background: "#FFFFFF",
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)",
    gap: "18px",
  },

  leftCol: {
    display: "grid",
    gap: "18px",
  },

  rightCol: {
    display: "grid",
    alignSelf: "start",
  },

  sectionCard: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: "18px",
    padding: "18px",
    boxShadow: "0 6px 20px rgba(15,23,42,0.04)",
  },

  sideCard: {
    background: "linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)",
    border: "1px solid #DBEAFE",
    borderRadius: "18px",
    padding: "18px",
    boxShadow: "0 6px 20px rgba(37,99,235,0.06)",
  },

  sectionHeader: {
    marginBottom: "18px",
  },

  sectionTitle: {
    margin: 0,
    fontSize: "17px",
    fontWeight: 700,
    color: "#0F172A",
  },

  sectionText: {
    margin: "6px 0 0",
    color: "#64748B",
    fontSize: "13px",
    lineHeight: 1.45,
  },

  inputGroup: {
    marginBottom: "16px",
  },

  label: {
    fontSize: "13px",
    color: "#334155",
    fontWeight: 700,
    marginBottom: "7px",
    display: "block",
  },

  input: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    borderRadius: "12px",
    border: "1px solid #CBD5E1",
    outline: "none",
    background: "#F8FAFC",
    boxSizing: "border-box",
    color: "#0F172A",
  },

  inputDisabled: {
    background: "#EEF2F7",
    color: "#64748B",
  },

  select: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    borderRadius: "12px",
    border: "1px solid #CBD5E1",
    outline: "none",
    background: "#F8FAFC",
    boxSizing: "border-box",
    color: "#0F172A",
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
  },

  switchCard: {
    border: "1px solid #E2E8F0",
    borderRadius: "16px",
    padding: "14px 16px",
    background: "#F8FAFC",
    display: "flex",
    justifyContent: "space-between",
    gap: "14px",
    alignItems: "center",
    cursor: "pointer",
  },

  switchTitle: {
    display: "block",
    fontSize: "14px",
    fontWeight: 700,
    color: "#0F172A",
  },

  switchText: {
    margin: "4px 0 0",
    fontSize: "12px",
    color: "#64748B",
    lineHeight: 1.45,
  },

  sideTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 700,
    color: "#0F172A",
  },

  previewItem: {
    marginTop: "14px",
    padding: "12px 14px",
    borderRadius: "14px",
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
  },

  previewLabel: {
    display: "block",
    color: "#64748B",
    fontSize: "12px",
    marginBottom: "6px",
    fontWeight: 600,
  },

  previewValue: {
    color: "#0F172A",
    fontSize: "14px",
    fontWeight: 700,
    wordBreak: "break-word",
  },

  kpiCard: {
    marginTop: "14px",
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: "16px",
    padding: "14px 16px",
  },

  kpiLabel: {
    display: "block",
    fontSize: "12px",
    color: "#64748B",
    fontWeight: 600,
    marginBottom: "8px",
  },

  kpiValue: {
    display: "block",
    fontSize: "18px",
    color: "#0F172A",
    fontWeight: 800,
  },

  kpiCardPrimary: {
    marginTop: "14px",
    background: "linear-gradient(180deg, #2563EB 0%, #1D4ED8 100%)",
    border: "1px solid #1D4ED8",
    borderRadius: "16px",
    padding: "16px",
    boxShadow: "0 10px 24px rgba(37,99,235,0.22)",
  },

  kpiLabelPrimary: {
    display: "block",
    fontSize: "12px",
    color: "rgba(255,255,255,0.82)",
    fontWeight: 700,
    marginBottom: "8px",
  },

  kpiValuePrimary: {
    display: "block",
    fontSize: "22px",
    color: "#FFFFFF",
    fontWeight: 900,
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "16px 26px 20px",
    borderTop: "1px solid #E5E7EB",
    background: "#FFFFFF",
  },

  cancelBtn: {
    padding: "11px 16px",
    background: "#E5E7EB",
    color: "#111827",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: 700,
  },

  saveBtn: {
    padding: "11px 18px",
    background: "#2563EB",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: 800,
    boxShadow: "0 8px 20px rgba(37,99,235,0.24)",
  },

  saveBtnDisabled: {
    opacity: 0.75,
    cursor: "not-allowed",
  },
};

export default ShippingRuleModal;